// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../../context/AuthContext'
import useInactivity from '../../hooks/useInactivity'
import {
  MdDashboard, MdSmartToy, MdBusiness, MdAttachMoney, MdNotifications, MdSettings,
  MdTrendingUp, MdWarning, MdStar, MdBolt, MdCardGiftcard, MdCategory,
  MdEdit, MdDelete, MdAdd,
  MdLightMode, MdDarkMode, MdLogout, MdChevronLeft, MdChevronRight,
  MdSearch, MdRefresh,
} from 'react-icons/md'

const API_URL = process.env.REACT_APP_API_URL || 'https://agent-ia-senegal.onrender.com'

// ── THÈMES ──
const THEMES = {
  dark: {
    name: 'Sombre',
    bg: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
    sidebar: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
    sidebarBorder: 'rgba(255,255,255,0.07)',
    header: 'rgba(15,23,42,0.85)',
    headerBorder: 'rgba(255,255,255,0.07)',
    card: 'rgba(30,27,75,0.6)',
    cardBorder: 'rgba(255,255,255,0.08)',
    text: '#f1f5f9',
    textSub: '#94a3b8',
    textMuted: '#475569',
    input: 'rgba(255,255,255,0.06)',
    inputBorder: 'rgba(255,255,255,0.1)',
    accent: '#3b82f6',
    navActive: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
    navHover: 'rgba(59,130,246,0.12)',
    userBox: 'rgba(255,255,255,0.05)',
    badge: 'rgba(255,255,255,0.08)',
  },
  light: {
    name: 'Clair',
    bg: '#f8fafc',
    sidebar: '#ffffff',
    sidebarBorder: 'rgba(0,0,0,0.07)',
    header: 'rgba(255,255,255,0.95)',
    headerBorder: 'rgba(0,0,0,0.07)',
    card: '#ffffff',
    cardBorder: 'rgba(0,0,0,0.08)',
    text: '#0f172a',
    textSub: '#475569',
    textMuted: '#94a3b8',
    input: '#f1f5f9',
    inputBorder: 'rgba(0,0,0,0.1)',
    accent: '#3b82f6',
    navActive: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
    navHover: 'rgba(59,130,246,0.08)',
    userBox: '#f8fafc',
    badge: '#f1f5f9',
  },
}

const formatPhone = (p) => {
  if (!p) return '—'
  const d = p.replace(/\D/g, '')
  if (d.startsWith('221') && d.length === 12)
    return '+221 ' + d.slice(3,5) + ' ' + d.slice(5,8) + ' ' + d.slice(8,10) + ' ' + d.slice(10,12)
  if (d.length === 9)
    return d.slice(0,2) + ' ' + d.slice(2,5) + ' ' + d.slice(5,7) + ' ' + d.slice(7,9)
  return p
}

const getPlanColor = (plan) => {
  if (plan === 'premium') return { bg: 'rgba(168,85,247,0.15)', color: '#a855f7' }
  if (plan === 'pro') return { bg: 'rgba(59,130,246,0.15)', color: '#3b82f6' }
  return { bg: 'rgba(100,116,139,0.15)', color: '#64748b' }
}

const getStatusColor = (b) => {
  const now = new Date()
  const trial = b.subscription?.trialEndDate ? new Date(b.subscription.trialEndDate) : null
  const expiry = b.subscription?.expiryDate ? new Date(b.subscription.expiryDate) : null
  const isPaid = b.subscription?.status === 'active' && expiry && expiry > now
  const isTrial = trial && trial > now && !isPaid
  if (!b.isActive) return { label: 'Bloqué', bg: 'rgba(239,68,68,0.15)', color: '#f87171' }
  if (isPaid) return { label: 'Actif', bg: 'rgba(16,185,129,0.15)', color: '#10b981' }
  if (isTrial) {
    const days = Math.ceil((trial - now) / 86400000)
    return { label: `Essai — ${days}j`, bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' }
  }
  return { label: 'Expiré', bg: 'rgba(239,68,68,0.12)', color: '#f87171' }
}

const AdminNotifications = ({ businesses, th, API_URL }) => {
  const [notifs, setNotifs] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [filter, setFilter] = React.useState('all')

  React.useEffect(() => {
        // Générer notifications depuis les businesses
        const generated = []
        const now = new Date()

        businesses.forEach(b => {
          const trialEnd = b.trialEnd ? new Date(b.trialEnd) : null
          const daysLeft = trialEnd ? Math.ceil((trialEnd - now) / (1000*60*60*24)) : null

          // Trial qui expire bientôt (≤ 3 jours)
          if (daysLeft !== null && daysLeft >= 0 && daysLeft <= 3 && b.plan === 'trial') {
            generated.push({
              id: b._id, type: 'trial_expiring', priority: 'high',
              icon: '⏰', color: '#ef4444',
              title: `Trial expire dans ${daysLeft}j — ${b.name}`,
              message: `${b.sector} • ${b.phone}`,
              time: new Date(),
              phone: b.phone,
              whatsappMsg: `Bonjour ${b.name} ! Votre période d'essai gratuit expire dans ${daysLeft} jour(s). Passez à un plan payant pour continuer à utiliser votre agent IA. 👉 ${process.env.REACT_APP_API_URL?.replace('/api','') || 'https://agent-ia-senegal.com'}`
            })
          }

          // Abonnement payant qui expire bientôt (≤ 7 jours)
          const expiryDate = b.subscription?.expiryDate ? new Date(b.subscription.expiryDate) : null
          if (expiryDate && b.subscription?.status === 'active') {
            const daysToExpiry = Math.ceil((expiryDate - now) / (1000*60*60*24))
            if (daysToExpiry >= 0 && daysToExpiry <= 7) {
              generated.push({
                id: b._id + '_expiry', type: 'subscription_expiring', priority: daysToExpiry <= 2 ? 'high' : 'normal',
                icon: '💳', color: daysToExpiry <= 2 ? '#ef4444' : '#f59e0b',
                title: `Abonnement ${b.plan} expire dans ${daysToExpiry}j — ${b.name}`,
                message: `${b.sector} • Renouvellement requis`,
                time: new Date(),
                phone: b.phone,
                whatsappMsg: `Bonjour ${b.name} ! Votre abonnement ${b.plan} expire dans ${daysToExpiry} jour(s). Renouvelez maintenant pour éviter l'interruption de votre agent IA. 👉 agent-ia-senegal.com`
              })
            }
          }
          // Nouveau business inscrit cette semaine
          const created = b.createdAt ? new Date(b.createdAt) : null
          if (created && (now - created) < 7 * 24 * 60 * 60 * 1000) {
            generated.push({
              id: b._id + '_new', type: 'new_business', priority: 'normal',
              icon: '🆕', color: '#10b981',
              title: `Nouvelle inscription — ${b.name}`,
              message: `${b.sector} • ${new Date(b.createdAt).toLocaleDateString('fr-FR')}`,
              time: new Date(b.createdAt)
            })
          }
          // Business actif sans messages depuis 7 jours (inactif)
          if (b.plan !== 'trial' && b.subscription?.status === 'active') {
            generated.push({
              id: b._id + '_active', type: 'active', priority: 'low',
              icon: '✅', color: '#8b5cf6',
              title: `Abonné actif — ${b.name}`,
              message: `Plan ${b.plan} • ${b.sector}`,
              time: new Date()
            })
          }
        })

        setNotifs(generated.sort((a,b) => {
          const p = { high: 0, normal: 1, low: 2 }
          return p[a.priority] - p[b.priority]
        }))
        setLoading(false)
      }, [businesses]) // eslint-disable-line react-hooks/exhaustive-deps

      const filtered = filter === 'all' ? notifs : notifs.filter(n => n.type === filter)
      const highCount = notifs.filter(n => n.priority === 'high').length

      return (
        <div style={{ padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div>
              <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: 0 }}>Notifications Admin</h2>
              <p style={{ color: th.textMuted, fontSize: 13, margin: '4px 0 0' }}>Alertes et événements de la plateforme</p>
            </div>
            {highCount > 0 && (
              <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '8px 16px' }}>
                <p style={{ color: '#f87171', fontWeight: 700, fontSize: 13, margin: 0 }}>⚠️ {highCount} alerte(s) urgente(s)</p>
              </div>
            )}
          </div>

          {/* Filtres */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[
              { id: 'all', label: `Toutes (${notifs.length})` },
              { id: 'trial_expiring', label: `⏰ Trials (${notifs.filter(n=>n.type==='trial_expiring').length})` },
              { id: 'new_business', label: `🆕 Nouvelles (${notifs.filter(n=>n.type==='new_business').length})` },
              { id: 'active', label: `✅ Actifs (${notifs.filter(n=>n.type==='active').length})` },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{
                  padding: '8px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: filter === f.id ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : th.card,
                  color: filter === f.id ? 'white' : th.textMuted,
                }}>
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <p style={{ fontSize: 40 }}>🔔</p>
              <p style={{ color: th.textMuted, marginTop: 8 }}>Aucune notification</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map((n, i) => (
                <div key={i} style={{
                  background: th.card, border: `1px solid ${n.priority === 'high' ? 'rgba(239,68,68,0.3)' : th.cardBorder}`,
                  borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14,
                  boxShadow: n.priority === 'high' ? '0 0 20px rgba(239,68,68,0.1)' : 'none'
                }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: n.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                    {n.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: th.text, fontWeight: 700, fontSize: 14, margin: 0, truncate: 'ellipsis' }}>{n.title}</p>
                    <p style={{ color: th.textMuted, fontSize: 12, margin: '2px 0 0' }}>{n.message}</p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, alignItems: 'flex-end' }}>
                    {n.priority === 'high' && (
                      <span style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8 }}>URGENT</span>
                    )}
                    {n.phone && n.whatsappMsg && (
                      <a href={'https://wa.me/' + n.phone.replace(/\D/g,'') + '?text=' + encodeURIComponent(n.whatsappMsg)}
                        target="_blank" rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ background: 'rgba(37,211,102,0.15)', color: '#25d366', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                        📱 WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
}

const AdminSettings = ({ businesses, th, API_URL, platformConfig, onSaved }) => {
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)
  const [config, setConfig] = React.useState(() => ({
    platformName: platformConfig?.platformName || 'Agent IA Sénégal',
    universalNumber: platformConfig?.universalNumber || '+221 78 827 02 02',
    trialDays: platformConfig?.trialDays ?? 14,
    starterPrice: platformConfig?.starterPrice ?? 15000,
    proPrice: platformConfig?.proPrice ?? 35000,
    premiumPrice: platformConfig?.premiumPrice ?? 65000,
    maxBusinesses: platformConfig?.maxBusinesses ?? 1000,
    maintenanceMode: platformConfig?.maintenanceMode ?? false,
    emailNotifications: platformConfig?.emailNotifications ?? true,
    autoDisableExpired: platformConfig?.autoDisableExpired ?? true,
    supportEmail: platformConfig?.supportEmail || 'support@lebinaire.com',
    supportPhone: platformConfig?.supportPhone || '+221 77 648 00 68',
  }))

  // Sync quand platformConfig arrive de l'API
  React.useEffect(() => {
    if (platformConfig) {
      setConfig({
        platformName: platformConfig.platformName || 'Agent IA Sénégal',
        universalNumber: platformConfig.universalNumber || '+221 78 827 02 02',
        trialDays: platformConfig.trialDays ?? 14,
        starterPrice: platformConfig.starterPrice ?? 15000,
        proPrice: platformConfig.proPrice ?? 35000,
        premiumPrice: platformConfig.premiumPrice ?? 65000,
        maxBusinesses: platformConfig.maxBusinesses ?? 1000,
        maintenanceMode: platformConfig.maintenanceMode ?? false,
        emailNotifications: platformConfig.emailNotifications ?? true,
        autoDisableExpired: platformConfig.autoDisableExpired ?? true,
        supportEmail: platformConfig.supportEmail || 'support@lebinaire.com',
        supportPhone: platformConfig.supportPhone || '+221 77 648 00 68',
      })
    }
  }, [platformConfig])

      const handleSave = async () => {
        setSaving(true)
        try {
          await axios.put(`${API_URL}/api/admin/config`, config)
          setSaved(true)
          setTimeout(() => setSaved(false), 3000)
          if (onSaved) onSaved() // Recharger les données
        } catch(e) {
          alert('Erreur sauvegarde: ' + e.message)
        } finally { setSaving(false) }
      }

      // Field non-contrôlé — onChange met à jour config seulement onBlur
      // Ainsi aucun re-render à chaque frappe → pas de perte de focus
      const Field = ({ label, value, onChange, type, hint, placeholder }) => (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', color: th.textMuted, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{label}</label>
          <input
            type={type || 'text'}
            defaultValue={value === undefined || value === null ? '' : String(value)}
            key={String(value) + label}
            onBlur={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
            placeholder={placeholder || ''}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
          />
          {hint && <p style={{ color: th.textMuted, fontSize: 11, marginTop: 4 }}>{hint}</p>}
        </div>
      )

      const Toggle = ({ label, value, onChange, desc }) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 12, marginBottom: 10 }}>
          <div>
            <p style={{ color: th.text, fontWeight: 600, fontSize: 14, margin: 0 }}>{label}</p>
            {desc && <p style={{ color: th.textMuted, fontSize: 12, margin: '2px 0 0' }}>{desc}</p>}
          </div>
          <button onClick={() => onChange(!value)}
            style={{ width: 44, height: 24, borderRadius: 12, background: value ? '#10b981' : '#475569', border: 'none', cursor: 'pointer', position: 'relative', transition: 'all 0.2s' }}>
            <span style={{ position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: 'all 0.2s', left: value ? 22 : 2 }}/>
          </button>
        </div>
      )

      return (
        <div style={{ padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
            <div>
              <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: 0 }}>Paramètres Admin</h2>
              <p style={{ color: th.textMuted, fontSize: 13, margin: '4px 0 0' }}>Configuration globale de la plateforme LeBinaire</p>
            </div>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '10px 24px', borderRadius: 12, background: saved ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saved ? '✅ Sauvegardé !' : saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            {/* Colonne 1 */}
            <div>
              <h3 style={{ color: th.text, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>🏢 Plateforme</h3>
              <Field label="Nom de la plateforme" value={config.platformName} onChange={v => setConfig(c => ({...c, platformName: v}))}/>
              <Field label="Numéro WhatsApp universel" value={config.universalNumber} onChange={v => setConfig(c => ({...c, universalNumber: v}))} type="tel" hint="Numéro connecté à l'API WhatsApp (Sendchamp/Meta)" placeholder="+221 78 827 02 02"/>
              <Field label="Durée essai gratuit (jours)" value={config.trialDays} onChange={v => setConfig(c => ({...c, trialDays: v}))} type="number"/>
              <Field label="Max entreprises" value={config.maxBusinesses} onChange={v => setConfig(c => ({...c, maxBusinesses: v}))} type="number"/>

              <h3 style={{ color: th.text, fontSize: 15, fontWeight: 700, margin: '24px 0 16px' }}>💰 Tarifs (FCFA/mois)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Starter', key: 'starterPrice', color: '#3b82f6' },
                  { label: 'Pro', key: 'proPrice', color: '#8b5cf6' },
                  { label: 'Premium', key: 'premiumPrice', color: '#f59e0b' },
                ].map(p => (
                  <div key={p.key}>
                    <label style={{ color: p.color, fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 6 }}>{p.label}</label>
                    <input type="number" value={config[p.key]} onChange={e => setConfig(c => ({...c, [p.key]: Number(e.target.value)}))}
                      style={{ width: '100%', padding: '10px', borderRadius: 10, border: `1px solid ${p.color}44`, background: p.color + '11', color: th.text, fontSize: 14, outline: 'none', fontWeight: 700, boxSizing: 'border-box', textAlign: 'center' }}/>
                  </div>
                ))}
              </div>
            </div>

            {/* Colonne 2 */}
            <div>
              <h3 style={{ color: th.text, fontSize: 15, fontWeight: 700, marginBottom: 16 }}>📧 Support</h3>
              <Field label="Email support" value={config.supportEmail} onChange={v => setConfig(c => ({...c, supportEmail: v}))} type="email"/>
              <Field label="Téléphone support" value={config.supportPhone} onChange={v => setConfig(c => ({...c, supportPhone: v}))} type="tel" placeholder="+221 77 648 00 68"/>

              <h3 style={{ color: th.text, fontSize: 15, fontWeight: 700, margin: '24px 0 16px' }}>⚙️ Comportement</h3>
              <Toggle label="Mode maintenance" value={config.maintenanceMode} onChange={v => setConfig(c => ({...c, maintenanceMode: v}))} desc="Bloque l'accès aux entrepreneurs"/>
              <Toggle label="Notifications email" value={config.emailNotifications} onChange={v => setConfig(c => ({...c, emailNotifications: v}))} desc="Alertes nouvelles inscriptions"/>
              <Toggle label="Désactivation auto expirés" value={config.autoDisableExpired} onChange={v => setConfig(c => ({...c, autoDisableExpired: v}))} desc="Désactive les comptes expirés automatiquement"/>

              {/* Infos système */}
              <div style={{ marginTop: 24, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 14, padding: 16 }}>
                <p style={{ color: '#60a5fa', fontWeight: 700, fontSize: 13, marginBottom: 10 }}>ℹ️ Infos système</p>
                {[
                  { label: 'Version', value: 'v1.0.0 — Agent IA Sénégal' },
                  { label: 'Backend', value: 'Render.com — Node.js' },
                  { label: 'Frontend', value: 'Vercel — React' },
                  { label: 'DB', value: 'MongoDB Atlas' },
                  { label: 'IA', value: 'Groq + OpenAI GPT-4o-mini' },
                  { label: 'Entreprises', value: `${businesses.length} inscrites` },
                ].map((info, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: th.textMuted, fontSize: 12 }}>{info.label}</span>
                    <span style={{ color: th.text, fontSize: 12, fontWeight: 600 }}>{info.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
}


// ─── COMPOSANT ADMIN SECTEURS ───
const AdminSectors = ({ th, API_URL }) => {
  const [sectors, setSectors] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [generating, setGenerating] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [generated, setGenerated] = React.useState([])
  const [selected, setSelected] = React.useState([])
  const [showAdd, setShowAdd] = React.useState(false)
  const [newLabel, setNewLabel] = React.useState('')
  const [newSubs, setNewSubs] = React.useState('')
  const [country, setCountry] = React.useState('SN')
  const [genCount, setGenCount] = React.useState(5)
  const [searchSector, setSearchSector] = React.useState('')
  const [editId, setEditId] = React.useState(null)
  const [editLabel, setEditLabel] = React.useState('')
  const [editSubs, setEditSubs] = React.useState('')
  const [msg, setMsg] = React.useState('')

  const loadSectors = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/api/sectors/all`)
      setSectors(res.data || [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  React.useEffect(() => { loadSectors() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const showMsg = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000) }

  const handleGenerate = async () => {
    setGenerating(true)
    setGenerated([])
    setSelected([])
    try {
      const res = await axios.post(`${API_URL}/api/sectors/generate`, { country, count: genCount })
      setGenerated(res.data.generated || [])
      setSelected(res.data.generated?.map((_, i) => i) || [])
      if (res.data.duplicatesRemoved > 0) showMsg(`ℹ️ ${res.data.duplicatesRemoved} doublon(s) supprimé(s) automatiquement`)
    } catch (e) { showMsg('❌ Erreur génération: ' + e.message) }
    finally { setGenerating(false) }
  }

  const handleSaveGenerated = async () => {
    const toSave = generated.filter((_, i) => selected.includes(i))
    if (!toSave.length) { showMsg('Sélectionnez au moins un secteur'); return }
    setSaving(true)
    try {
      const res = await axios.post(`${API_URL}/api/sectors/bulk`, { sectors: toSave, country })
      showMsg(`✅ ${res.data.created} secteur(s) ajouté(s)${res.data.skipped > 0 ? `, ${res.data.skipped} ignoré(s)` : ''}`)
      setGenerated([])
      setSelected([])
      loadSectors()
    } catch (e) { showMsg('❌ ' + e.message) }
    finally { setSaving(false) }
  }

  const handleAdd = async () => {
    if (!newLabel.trim()) return
    try {
      const subs = newSubs.split('\n').map(s => s.trim()).filter(Boolean)
      await axios.post(`${API_URL}/api/sectors`, { label: newLabel.trim(), subcategories: subs, country })
      showMsg('✅ Secteur ajouté')
      setNewLabel(''); setNewSubs(''); setShowAdd(false)
      loadSectors()
    } catch (e) { showMsg('❌ ' + (e.response?.data?.error || e.message)) }
  }

  const handleEdit = async (id) => {
    try {
      const subs = editSubs.split('\n').map(s => s.trim()).filter(Boolean)
      await axios.put(`${API_URL}/api/sectors/${id}`, { label: editLabel, subcategories: subs })
      showMsg('✅ Modifié')
      setEditId(null)
      loadSectors()
    } catch (e) { showMsg('❌ ' + e.message) }
  }

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Supprimer "${label}" ?`)) return
    try {
      await axios.delete(`${API_URL}/api/sectors/${id}`)
      showMsg('✅ Supprimé')
      loadSectors()
    } catch (e) { showMsg('❌ ' + e.message) }
  }

  const countryFiltered = sectors
    .filter(s => s.country === country)
    .filter(s => {
      if (!searchSector) return true
      const q = searchSector.toLowerCase()
      return s.label.toLowerCase().includes(q) || s.subcategories?.some(sub => sub.toLowerCase().includes(q))
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'))

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: 0 }}>Gestion des Secteurs</h2>
          <p style={{ color: th.textMuted, fontSize: 13, margin: '4px 0 0' }}>{countryFiltered.length} secteur(s) · triés A→Z</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={country} onChange={e => setCountry(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 13 }}>
            <option value="SN">🇸🇳 Sénégal</option>
            <option value="ML">🇲🇱 Mali</option>
            <option value="CI">🇨🇮 Côte d'Ivoire</option>
          </select>
          <button onClick={() => setShowAdd(!showAdd)}
            style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <MdAdd size={16}/> Ajouter
          </button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: th.card, border: '1px solid ' + th.cardBorder, borderRadius: 12, padding: '0 14px', marginBottom: 20 }}>
        <MdSearch size={18} style={{ color: th.textMuted, flexShrink: 0 }}/>
        <input value={searchSector} onChange={e => setSearchSector(e.target.value)}
          placeholder="Rechercher un secteur ou sous-catégorie..."
          style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: th.text, fontSize: 14, padding: '12px 0' }}/>
        {searchSector && <button onClick={() => setSearchSector('')} style={{ background: 'none', border: 'none', color: th.textMuted, cursor: 'pointer', fontSize: 18 }}>×</button>}
      </div>

      {/* Message feedback */}
      {msg && <div style={{ background: msg.startsWith('❌') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', border: '1px solid ' + (msg.startsWith('❌') ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'), borderRadius: 10, padding: '10px 16px', marginBottom: 16, color: msg.startsWith('❌') ? '#f87171' : '#10b981', fontSize: 13, fontWeight: 600 }}>{msg}</div>}

      {/* Formulaire ajout manuel */}
      {showAdd && (
        <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
          <p style={{ color: '#818cf8', fontWeight: 700, fontSize: 14, marginBottom: 12 }}>➕ Nouveau secteur</p>
          <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Nom du secteur *"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 14, outline: 'none', marginBottom: 10, boxSizing: 'border-box' }}/>
          <textarea value={newSubs} onChange={e => setNewSubs(e.target.value)} rows={4}
            placeholder={"Sous-catégories (une par ligne):\nEx: Boutique / Vente\nImport-Export"}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}/>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleAdd} style={{ padding: '10px 20px', borderRadius: 10, background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: 'white', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Ajouter</button>
            <button onClick={() => setShowAdd(false)} style={{ padding: '10px 20px', borderRadius: 10, background: th.cardBorder, color: th.textMuted, border: 'none', fontSize: 13, cursor: 'pointer' }}>Annuler</button>
          </div>
        </div>
      )}

      {/* Section Génération IA */}
      <div style={{ background: 'rgba(40,97,227,0.06)', border: '1px solid rgba(40,97,227,0.2)', borderRadius: 14, padding: 20, marginBottom: 24 }}>
        <p style={{ color: '#2561e3', fontWeight: 700, fontSize: 14, marginBottom: 12 }}>✨ Générer avec l'IA</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ color: th.textMuted, fontSize: 13 }}>Générer</span>
          <select value={genCount} onChange={e => setGenCount(Number(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 13 }}>
            {[3,5,8,10].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ color: th.textMuted, fontSize: 13 }}>nouveaux secteurs pour {country}</span>
          <button onClick={handleGenerate} disabled={generating}
            style={{ padding: '8px 20px', borderRadius: 10, background: 'linear-gradient(135deg,#2561e3,#2357de)', color: 'white', border: 'none', fontWeight: 700, fontSize: 13, cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.7 : 1 }}>
            {generating ? '⏳ Génération...' : '✨ Générer'}
          </button>
        </div>
        <p style={{ color: th.textMuted, fontSize: 12 }}>L'IA évite automatiquement les doublons avec les {countryFiltered.length} secteurs existants.</p>

        {/* Résultats générés */}
        {generated.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <p style={{ color: th.text, fontWeight: 700, fontSize: 13 }}>{generated.length} secteur(s) généré(s) — sélectionnez ceux à garder :</p>
              <button onClick={() => setSelected(selected.length === generated.length ? [] : generated.map((_, i) => i))}
                style={{ fontSize: 12, color: '#2561e3', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                {selected.length === generated.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {generated.map((s, i) => (
                <div key={i} onClick={() => setSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])}
                  style={{ background: selected.includes(i) ? 'rgba(40,97,227,0.08)' : th.card, border: '1px solid ' + (selected.includes(i) ? 'rgba(40,97,227,0.4)' : th.cardBorder), borderRadius: 10, padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 6, border: '2px solid ' + (selected.includes(i) ? '#2561e3' : th.cardBorder), background: selected.includes(i) ? '#2561e3' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    {selected.includes(i) && <span style={{ color: 'white', fontSize: 12 }}>✓</span>}
                  </div>
                  <div>
                    <p style={{ color: th.text, fontWeight: 700, fontSize: 13, margin: 0 }}>{s.label}</p>
                    <p style={{ color: th.textMuted, fontSize: 12, margin: '4px 0 0' }}>{s.subcategories?.join(' · ')}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleSaveGenerated} disabled={saving || !selected.length}
              style={{ marginTop: 12, padding: '10px 24px', borderRadius: 10, background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: saving || !selected.length ? 0.6 : 1 }}>
              {saving ? 'Sauvegarde...' : `✅ Ajouter ${selected.length} secteur(s)`}
            </button>
          </div>
        )}
      </div>

      {/* Liste des secteurs existants */}
      {loading ? (
        <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {countryFiltered.map(sector => (
            <div key={sector._id} style={{ background: th.card, border: '1px solid ' + th.cardBorder, borderRadius: 14, padding: '14px 18px' }}>
              {editId === sector._id ? (
                <div>
                  <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 14, outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}/>
                  <textarea value={editSubs} onChange={e => setEditSubs(e.target.value)} rows={4}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid ' + th.cardBorder, background: th.bg, color: th.text, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}/>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleEdit(sector._id)} style={{ padding: '7px 16px', borderRadius: 8, background: '#10b981', color: 'white', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Sauvegarder</button>
                    <button onClick={() => setEditId(null)} style={{ padding: '7px 16px', borderRadius: 8, background: th.cardBorder, color: th.textMuted, border: 'none', fontSize: 12, cursor: 'pointer' }}>Annuler</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: th.text, fontWeight: 700, fontSize: 14, margin: 0 }}>{sector.label}</p>
                    <p style={{ color: th.textMuted, fontSize: 12, margin: '4px 0 0', lineHeight: 1.6 }}>
                      {sector.subcategories?.slice(0, 4).join(' · ')}{sector.subcategories?.length > 4 ? ` +${sector.subcategories.length - 4}` : ''}
                    </p>
                    <span style={{ fontSize: 11, color: '#2561e3', fontWeight: 600 }}>{sector.subcategories?.length || 0} sous-catégories</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => { setEditId(sector._id); setEditLabel(sector.label); setEditSubs(sector.subcategories?.join('\n') || '') }}
                      style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MdEdit size={15}/>
                    </button>
                    <button onClick={() => handleDelete(sector._id, sector.label)}
                      style={{ padding: '6px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#f87171', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MdDelete size={15}/>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {countryFiltered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <p style={{ fontSize: 40 }}>📂</p>
              <p style={{ color: th.textMuted, marginTop: 8 }}>Aucun secteur pour {country}</p>
              <p style={{ color: th.textMuted, fontSize: 13 }}>Utilisez la génération IA ou ajoutez manuellement</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AdminDashboard() {
  const { logout } = useAuth()
  useInactivity() // Auto-déconnexion après 10 min d'inactivité
  const [theme, setTheme] = useState(localStorage.getItem('admin-theme') || 'dark')
  const th = THEMES[theme]
  const isDark = theme === 'dark'

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark'
    setTheme(next)
    localStorage.setItem('admin-theme', next)
  }

  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [businesses, setBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [platformConfig, setPlatformConfig] = useState(null)
  const [search, setSearch] = useState('')
  const [filterPlan, setFilterPlan] = useState('all')
  const [filterSector, setFilterSector] = useState('all')
  const [activeTab, setActiveTab] = useState('overview')
  const [selected, setSelected] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)
  const [toast, setToast] = useState(null)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  const loadBusinesses = useCallback(async () => {
    try {
      const [bizRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/businesses`),
        axios.get(`${API_URL}/api/admin/config`).catch(() => ({ data: null }))
      ])
      setBusinesses(bizRes.data || [])
      if (configRes.data) setPlatformConfig(configRes.data)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadBusinesses() }, [loadBusinesses])

  // Bloquer navigation arrière
  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname)
    const handlePopState = () => window.history.pushState(null, '', window.location.pathname)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const navItems = [
    { id: 'overview', icon: <MdDashboard size={20}/>, label: 'Vue d\'ensemble', color: '#3b82f6' },
    { id: 'businesses', icon: <MdBusiness size={20}/>, label: 'Entreprises', color: '#8b5cf6' },
    { id: 'revenue', icon: <MdAttachMoney size={20}/>, label: 'Revenus', color: '#10b981' },
    { id: 'notifications', icon: <MdNotifications size={20}/>, label: 'Notifications', color: '#f59e0b' },
    { id: 'settings', icon: <MdSettings size={20}/>, label: 'Paramètres', color: '#6366f1' },
    { id: 'sectors', icon: <MdCategory size={20}/>, label: 'Secteurs', color: '#2561e3' },
  ]

  const now = new Date()
  const stats = {
    total: businesses.length,
    trial: businesses.filter(b => {
      const t = b.subscription?.trialEndDate ? new Date(b.subscription.trialEndDate) : null
      return t && t > now && b.subscription?.status !== 'active'
    }).length,
    active: businesses.filter(b => {
      const e = b.subscription?.expiryDate ? new Date(b.subscription.expiryDate) : null
      return b.subscription?.status === 'active' && e && e > now
    }).length,
    expired: businesses.filter(b => {
      const e = b.subscription?.expiryDate ? new Date(b.subscription.expiryDate) : null
      const t = b.subscription?.trialEndDate ? new Date(b.subscription.trialEndDate) : null
      const isPaid = b.subscription?.status === 'active' && e && e > now
      const isTrial = t && t > now
      return !isPaid && !isTrial
    }).length,
  }

  // Secteurs uniques depuis les businesses
  const uniqueSectors = [...new Set(businesses.map(b => b.sector?.split(' — ')[0]).filter(Boolean))].sort()

  const filtered = businesses.filter(b => {
    const q = search.toLowerCase()
    const matchSearch = !q || b.name?.toLowerCase().includes(q) || formatPhone(b.phone).includes(q) || b.sector?.toLowerCase().includes(q)
    const matchPlan = filterPlan === 'all' || b.plan === filterPlan
    return matchSearch && matchPlan && (filterSector === 'all' || b.sector?.startsWith(filterSector))
  })

  // Actions admin
  const handleAction = async (action, id, extra = {}) => {
    setActionLoading(id + action)
    try {
      const now = new Date()
      if (action === 'activate') {
        const expiry = new Date(now); expiry.setMonth(expiry.getMonth() + 1)
        await axios.put(`${API_URL}/api/businesses/${id}/company`, { 'subscription.status': 'active', 'subscription.expiryDate': expiry, isActive: true })
        showToast('Compte activé 1 mois')
      } else if (action === 'trial') {
        const trial = new Date(now); trial.setDate(trial.getDate() + 7)
        await axios.put(`${API_URL}/api/businesses/${id}/company`, { 'subscription.trialEndDate': trial })
        showToast('+7 jours essai ajoutés')
      } else if (action === 'plan') {
        await axios.put(`${API_URL}/api/businesses/${id}/company`, { plan: extra.plan })
        showToast('Plan mis à jour')
      } else if (action === 'block') {
        // Utilise la route admin qui notifie aussi les clients WhatsApp
        await axios.put(`${API_URL}/api/admin/businesses/${id}/block`, {
          blocked: extra.isActive, // si isActive=true on bloque, si false on débloque
          reason: extra.reason || ''
        })
        showToast(extra.isActive ? '⛔ Compte bloqué — clients notifiés' : '✅ Compte débloqué')
      } else if (action === 'delete') {
        if (!window.confirm('Supprimer définitivement ce compte ? Action irréversible.')) return
        await axios.delete(`${API_URL}/api/admin/businesses/${id}`)
        setSelected(null)
        showToast('Compte supprimé')
      }
      await loadBusinesses()
    } catch (e) { showToast('Erreur: ' + e.message, 'error') }
    finally { setActionLoading(null) }
  }

  const PLAN_PRICES = { starter: 15000, pro: 35000, premium: 65000 }
  const monthlyRevenue = businesses.reduce((sum, b) => {
    const e = b.subscription?.expiryDate ? new Date(b.subscription.expiryDate) : null
    const isPaid = b.subscription?.status === 'active' && e && e > now
    return isPaid ? sum + (PLAN_PRICES[b.plan] || 0) : sum
  }, 0)

  const kpiCards = [
    { icon: <MdBusiness size={24}/>, label: 'Total entreprises', value: stats.total, sub: 'Sur la plateforme', color: '#3b82f6' },
    { icon: <MdCardGiftcard size={24}/>, label: 'En essai', value: stats.trial, sub: 'Période gratuite', color: '#10b981' },
    { icon: <MdBolt size={24}/>, label: 'Abonnés actifs', value: stats.active, sub: 'Plans payants', color: '#8b5cf6' },
    { icon: <MdWarning size={24}/>, label: 'Expirés', value: stats.expired, sub: 'À relancer', color: '#f59e0b' },
  ]

  // ── SIDEBAR ──
  const Sidebar = () => (
    <div style={{
      width: sidebarOpen ? 240 : 68, flexShrink: 0,
      background: th.sidebar,
      borderRight: `1px solid ${th.sidebarBorder}`,
      display: 'flex', flexDirection: 'column',
      transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
      overflow: 'hidden',
      position: 'fixed', top: 0, left: 0,
      height: '100vh', zIndex: 40,
    }}>
      {/* Logo */}
      <div style={{ height: 64, display: 'flex', alignItems: 'center', padding: sidebarOpen ? '0 16px' : '0', justifyContent: sidebarOpen ? 'flex-start' : 'center', borderBottom: `1px solid ${th.sidebarBorder}`, gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <MdSmartToy size={22} style={{ color: 'white' }}/>
        </div>
        {sidebarOpen && (
          <div>
            <p style={{ fontWeight: 800, fontSize: 15, color: th.text, letterSpacing: '-0.3px', lineHeight: 1.2 }}>Agent IA</p>
            <p style={{ fontSize: 11, color: '#3b82f6', fontWeight: 700, letterSpacing: '0.5px' }}>by LeBinaire</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {navItems.map(item => (
          <button key={item.id} onClick={() => setActiveTab(item.id)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center',
              gap: sidebarOpen ? 10 : 0,
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              padding: sidebarOpen ? '10px 12px' : '10px 0',
              borderRadius: 12, border: 'none', cursor: 'pointer',
              transition: 'all 0.15s',
              background: activeTab === item.id ? th.navActive : 'transparent',
              color: activeTab === item.id ? 'white' : th.textSub,
            }}
            onMouseEnter={e => { if (activeTab !== item.id) e.currentTarget.style.background = th.navHover }}
            onMouseLeave={e => { if (activeTab !== item.id) e.currentTarget.style.background = 'transparent' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: activeTab === item.id ? 'white' : item.color, background: activeTab === item.id ? 'rgba(255,255,255,0.15)' : item.color + '18' }}>
              {item.icon}
            </div>
            {sidebarOpen && <span style={{ fontSize: 14, fontWeight: 500 }}>{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* Bas sidebar : toggle thème + déconnexion */}
      <div style={{ padding: '8px', borderTop: `1px solid ${th.sidebarBorder}` }}>
        {/* Toggle thème */}
        <button onClick={toggleTheme}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: sidebarOpen ? 10 : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center', padding: sidebarOpen ? '10px 12px' : '10px 0', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'transparent', color: th.textSub, transition: 'all 0.15s', marginBottom: 4 }}
          onMouseEnter={e => e.currentTarget.style.background = th.navHover}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: th.input }}>
            {isDark ? <MdLightMode size={18} style={{ color: '#f59e0b' }}/> : <MdDarkMode size={18} style={{ color: '#6366f1' }}/>}
          </div>
          {sidebarOpen && <span style={{ fontSize: 14 }}>{isDark ? 'Thème clair' : 'Thème sombre'}</span>}
        </button>

        {/* Déconnexion */}
        <button onClick={() => { logout(); window.location.href = '/' }}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: sidebarOpen ? 10 : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center', padding: sidebarOpen ? '10px 12px' : '10px 0', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'transparent', color: '#f87171', transition: 'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: 'rgba(239,68,68,0.1)' }}>
            <MdLogout size={18} style={{ color: '#f87171' }}/>
          </div>
          {sidebarOpen && <span style={{ fontSize: 14, fontWeight: 600 }}>Déconnexion</span>}
        </button>
      </div>
    </div>
  )

  // ── HEADER ──
  const Header = () => (
    <div style={{ height: 64, background: th.header, borderBottom: `1px solid ${th.headerBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', position: 'sticky', top: 0, zIndex: 40, backdropFilter: 'blur(12px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => setSidebarOpen(s => !s)}
          style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.input, color: th.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {sidebarOpen ? <MdChevronLeft size={20}/> : <MdChevronRight size={20}/>}
        </button>
        <div>
          <p style={{ fontWeight: 700, fontSize: 16, color: th.text }}>{navItems.find(n => n.id === activeTab)?.label || 'Vue d\'ensemble'}</p>
          <p style={{ fontSize: 12, color: th.textMuted }}>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 15 }}>M</div>
      </div>
    </div>
  )

  // ── OVERVIEW ──
  const Overview = () => (
    <div style={{ padding: 28 }}>
      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 28 }}>
        {kpiCards.map((s, i) => (
          <div key={i} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, backdropFilter: 'blur(10px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}>
                {s.icon}
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: s.color, background: s.color + '15', padding: '3px 10px', borderRadius: 20 }}>{s.sub}</span>
            </div>
            <p style={{ fontSize: 36, fontWeight: 800, color: th.text, lineHeight: 1 }}>{s.value}</p>
            <p style={{ fontSize: 13, color: th.textMuted, marginTop: 6 }}>{s.label}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        {/* Revenus */}
        <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, backdropFilter: 'blur(10px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <p style={{ fontWeight: 700, fontSize: 15, color: th.text }}>Revenus mensuels</p>
            <span style={{ fontSize: 12, color: th.textMuted }}>FCFA</span>
          </div>
          <p style={{ fontSize: 40, fontWeight: 800, color: '#10b981' }}>{monthlyRevenue.toLocaleString('fr-FR')}</p>
          <p style={{ fontSize: 13, color: th.textMuted, marginTop: 8 }}>Basé sur {stats.active} abonnement(s) actif(s)</p>
          <div style={{ marginTop: 16, height: 4, borderRadius: 4, background: th.input }}>
            <div style={{ width: stats.total > 0 ? `${(stats.active/stats.total)*100}%` : '0%', height: '100%', background: 'linear-gradient(90deg,#10b981,#3b82f6)', borderRadius: 4 }}/>
          </div>
          <p style={{ fontSize: 12, color: th.textMuted, marginTop: 6 }}>{stats.total > 0 ? Math.round((stats.active/stats.total)*100) : 0}% de conversion</p>
        </div>

        {/* Répartition plans */}
        <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, backdropFilter: 'blur(10px)' }}>
          <p style={{ fontWeight: 700, fontSize: 15, color: th.text, marginBottom: 20 }}>Répartition par plan</p>
          {['starter', 'pro', 'premium'].map(plan => {
            const count = businesses.filter(b => b.plan === plan).length
            const pct = businesses.length > 0 ? Math.round((count/businesses.length)*100) : 0
            const colors = { starter: '#3b82f6', pro: '#8b5cf6', premium: '#f59e0b' }
            return (
              <div key={plan} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: colors[plan], textTransform: 'capitalize' }}>{plan}</span>
                  <span style={{ fontSize: 13, color: th.textMuted }}>{count} · {pct}%</span>
                </div>
                <div style={{ height: 6, borderRadius: 4, background: th.input }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: colors[plan], borderRadius: 4, transition: 'width 0.5s' }}/>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Dernières inscriptions */}
      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, backdropFilter: 'blur(10px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <p style={{ fontWeight: 700, fontSize: 15, color: th.text }}>Dernières inscriptions</p>
          <button onClick={() => setActiveTab('businesses')} style={{ fontSize: 13, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Voir tout →</button>
        </div>
        {businesses.slice(0, 5).map((b, i) => {
          const planC = getPlanColor(b.plan)
          const statusC = getStatusColor(b)
          return (
            <div key={i} onClick={() => setSelected(b)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: i < 4 ? `1px solid ${th.cardBorder}` : 'none', cursor: 'pointer' }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: '#3b82f622', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#3b82f6', fontSize: 16, flexShrink: 0 }}>
                {b.name?.[0]?.toUpperCase() || 'B'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 600, fontSize: 14, color: th.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name}</p>
                <p style={{ fontSize: 12, color: th.textMuted }}>{formatPhone(b.phone)} · {b.sector}</p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: planC.bg, color: planC.color, textTransform: 'capitalize' }}>{b.plan}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: statusC.bg, color: statusC.color }}>{statusC.label}</span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  // ── LISTE ENTREPRISES ──
  const BusinessList = () => (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 12, padding: '0 14px' }}>
          <MdSearch size={18} style={{ color: th.textMuted, flexShrink: 0 }}/>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, téléphone, secteur..."
            style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: th.text, fontSize: 14, padding: '12px 0' }}/>
        </div>
        {['all', 'starter', 'pro', 'premium'].map(p => (
          <button key={p} onClick={() => setFilterPlan(p)}
            style={{ padding: '10px 16px', borderRadius: 12, border: `1px solid ${filterPlan === p ? '#3b82f6' : th.cardBorder}`, background: filterPlan === p ? '#3b82f6' : th.card, color: filterPlan === p ? 'white' : th.textSub, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
            {p === 'all' ? 'Tous' : p}
          </button>
        ))}
        <select value={filterSector} onChange={e => setFilterSector(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: 12, border: `1px solid ${th.cardBorder}`, background: th.card, color: th.textSub, fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="all">Tous secteurs</option>
          {uniqueSectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={loadBusinesses} style={{ padding: '10px 14px', borderRadius: 12, border: `1px solid ${th.cardBorder}`, background: th.card, color: th.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <MdRefresh size={18}/>
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map((b, i) => {
          const planC = getPlanColor(b.plan)
          const statusC = getStatusColor(b)
          const isLoading = actionLoading?.startsWith(b._id)
          return (
            <div key={i} onClick={() => setSelected(selected?._id === b._id ? null : b)}
              style={{ background: th.card, border: `1px solid ${selected?._id === b._id ? '#3b82f6' : th.cardBorder}`, borderRadius: 16, padding: '16px 20px', cursor: 'pointer', transition: 'all 0.15s', backdropFilter: 'blur(10px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 13, background: '#3b82f622', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#3b82f6', fontSize: 18, flexShrink: 0 }}>
                  {b.name?.[0]?.toUpperCase() || 'B'}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, fontSize: 15, color: th.text }}>{b.name}</p>
                  <p style={{ fontSize: 13, color: th.textMuted }}>{formatPhone(b.phone)} · {b.sector}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: planC.bg, color: planC.color, textTransform: 'capitalize' }}>{b.plan}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: statusC.bg, color: statusC.color }}>{statusC.label}</span>
                </div>
              </div>

              {selected?._id === b._id && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${th.cardBorder}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
                    {[
                      { l: 'FAQ', v: b.modules?.faq?.length || 0 },
                      { l: 'Catalogue', v: b.modules?.catalog?.length || 0 },
                      { l: 'Compétences', v: b.modules?.skills?.length || 0 },
                    ].map((item, j) => (
                      <div key={j} style={{ background: th.input, borderRadius: 10, padding: '10px 14px', textAlign: 'center' }}>
                        <p style={{ fontSize: 20, fontWeight: 700, color: th.text }}>{item.v}</p>
                        <p style={{ fontSize: 11, color: th.textMuted }}>{item.l}</p>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/* Changer plan */}
                    {['starter', 'pro', 'premium'].map(plan => (
                      <button key={plan} onClick={e => { e.stopPropagation(); handleAction('plan', b._id, { plan }) }} disabled={!!isLoading || b.plan === plan}
                        style={{ padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: b.plan === plan ? 'default' : 'pointer', border: 'none', background: b.plan === plan ? getPlanColor(plan).bg : th.input, color: b.plan === plan ? getPlanColor(plan).color : th.textSub, textTransform: 'capitalize', opacity: isLoading ? 0.5 : 1 }}>
                        {plan}
                      </button>
                    ))}
                    <div style={{ width: 1, background: th.cardBorder }}/>
                    <button onClick={e => { e.stopPropagation(); handleAction('activate', b._id) }} disabled={!!isLoading}
                      style={{ padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'rgba(16,185,129,0.15)', color: '#10b981', opacity: isLoading ? 0.5 : 1 }}>
                      Activer 1 mois
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleAction('trial', b._id) }} disabled={!!isLoading}
                      style={{ padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', opacity: isLoading ? 0.5 : 1 }}>
                      +7j essai
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleAction('block', b._id, { isActive: b.isActive }) }} disabled={!!isLoading}
                      style={{ padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: b.isActive ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)', color: b.isActive ? '#f87171' : '#34d399', opacity: isLoading ? 0.5 : 1 }}>
                      {b.isActive ? 'Bloquer' : 'Débloquer'}
                    </button>
                    <button onClick={e => { e.stopPropagation(); handleAction('delete', b._id) }} disabled={!!isLoading}
                      style={{ padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'rgba(239,68,68,0.12)', color: '#f87171', opacity: isLoading ? 0.5 : 1 }}>
                      Supprimer
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
        {filtered.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: th.textMuted }}>
            <MdBusiness size={48} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }}/>
            <p>Aucune entreprise trouvée</p>
          </div>
        )}
      </div>
    </div>
  )

  // ── REVENUS ──
  const Revenue = () => {
    const planData = ['starter', 'pro', 'premium'].map(plan => {
      const count = businesses.filter(b => {
        const e = b.subscription?.expiryDate ? new Date(b.subscription.expiryDate) : null
        return b.plan === plan && b.subscription?.status === 'active' && e && e > now
      }).length
      return { plan, count, revenue: count * (PLAN_PRICES[plan] || 0) }
    })
    return (
      <div style={{ padding: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginBottom: 28 }}>
          {planData.map(({ plan, count, revenue }) => {
            const planC = getPlanColor(plan)
            const icon = plan === 'starter' ? <MdTrendingUp size={24}/> : plan === 'pro' ? <MdBolt size={24}/> : <MdStar size={24}/>
            return (
              <div key={plan} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, backdropFilter: 'blur(10px)' }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: planC.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: planC.color, marginBottom: 16 }}>
                  {icon}
                </div>
                <p style={{ fontSize: 13, color: th.textMuted, textTransform: 'capitalize', marginBottom: 4 }}>Plan {plan}</p>
                <p style={{ fontSize: 28, fontWeight: 800, color: th.text }}>{count} <span style={{ fontSize: 14, color: th.textMuted }}>abonnés</span></p>
                <p style={{ fontSize: 16, fontWeight: 700, color: planC.color, marginTop: 8 }}>{revenue.toLocaleString('fr-FR')} FCFA</p>
              </div>
            )
          })}
        </div>
        <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, backdropFilter: 'blur(10px)' }}>
          <p style={{ fontWeight: 700, fontSize: 15, color: th.text, marginBottom: 4 }}>Total mensuel estimé</p>
          <p style={{ fontSize: 48, fontWeight: 800, color: '#10b981' }}>{monthlyRevenue.toLocaleString('fr-FR')} <span style={{ fontSize: 16, color: th.textMuted }}>FCFA</span></p>
        </div>
      </div>
    )
  }

  const tabContent = {
    overview: <Overview/>,
    businesses: <BusinessList/>,
    revenue: <Revenue/>,
    notifications: <AdminNotifications businesses={businesses} th={th} API_URL={API_URL}/>,
    settings: <AdminSettings businesses={businesses} th={th} API_URL={API_URL} platformConfig={platformConfig} onSaved={loadBusinesses}/>,
    sectors: <AdminSectors th={th} API_URL={API_URL}/>
  }
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: th.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <Sidebar/>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: '100vh', marginLeft: sidebarOpen ? 240 : 68, transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)' }}>
        <Header/>
        <main style={{ flex: 1 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
              <div style={{ width: 40, height: 40, border: '3px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
            </div>
          ) : tabContent[activeTab]}
        </main>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: toast.type === 'error' ? '#ef4444' : '#10b981', color: 'white', padding: '12px 20px', borderRadius: 12, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s' }}>
          {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; margin: 0; padding: 0 }
        input::placeholder { color: inherit; opacity: 0.4 }
      `}</style>
    </div>
  )
}
